import { useAutoSubmit } from '@core/shared';
import { ChatSettings, OptionsApi } from '@core/swagger-client';
import {
    CompositePageHeader,
    ContainerNoTopSpace,
    ContainerWebinarInner,
    ContentHeader,
    ErrorMessagePage,
    LoaderPage,
    PageHeader,
    PageHeaderContent,
    Switch
} from '@core/ui';
import { useParams } from '@online-webinar/router';
import { BackToOriginLink } from '@online-webinar/ui-frontend';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

export const ChatSettingsPage = () => {
    const { webinarId } = useParams({ strict: false });
    const { t } = useTranslation();

    const { register, reset, trigger, watch, handleSubmit } = useForm<ChatSettings>({ mode: 'all', reValidateMode: 'onChange' });

    const setChatSettingsMutation = useMutation(OptionsApi.chat.setChatSettings());
    const onSubmit: SubmitHandler<ChatSettings> = (data) => {
        setChatSettingsMutation.mutate(
            { webinarId: webinarId!, settings: data },
            {
                onSuccess: () => {
                    reset(data);
                }
            }
        );
    };

    useAutoSubmit({
        trigger,
        watch,
        onSubmit: handleSubmit(onSubmit)
    });

    const { data: webinar, error: webinarError } = useQuery(OptionsApi.webinar.get(webinarId!, { staleTime: 60_000 }));
    const { data: chatSettings, error: chatSettingsError } = useQuery(OptionsApi.chat.getChatSettings(webinarId!));

    useEffect(() => {
        if (chatSettings) {
            // The response can land after the user has already flipped the toggle; without keepDirtyValues
            // the reset discarded that click and the debounced auto-save then posted the old value back.
            reset(chatSettings, { keepDirtyValues: true });
        }
    }, [chatSettings]);

    const error = webinarError || chatSettingsError;
    if (error) {
        return <ErrorMessagePage apiError={error} />;
    }

    if (!webinar) {
        return <LoaderPage />;
    }

    return (
        <ContainerNoTopSpace>
            <PageHeader>
                <PageHeaderContent>{webinar && <CompositePageHeader title={webinar.title} broadcastDateTime={webinar.broadcastDateTime} />}</PageHeaderContent>
            </PageHeader>
            <ContainerWebinarInner>
                <BackToOriginLink fallback="content" />
                <ContentHeader testId="chatSettings" contentHeader={t('ChatSettings.PageHeading')} />
                <form onSubmit={handleSubmit(onSubmit)}>
                    <div className="flex-auto mt-5">
                        <Switch classNames="mb-6" label={t('ChatSettings.OnDemandChat.Switch.Label')} {...register('enableOnDemandChatForwarding')} />
                    </div>
                </form>
            </ContainerWebinarInner>
        </ContainerNoTopSpace>
    );
};
